/**
 * Ore Refining API
 * GET /rakeback - fetch ore data
 * POST /rakeback-claim - refine available ore
 */

// =============================================================================
// TYPES
// =============================================================================

export type OreDepositStatus = 'locked' | 'available' | 'refined' | 'expired'

export interface OreDepositData {
  id: string
  amount: number
  status: OreDepositStatus
  activatesAt: string
  expiresAt: string
  createdAt: string
}

export interface OreSummary {
  totalLockedOre: number
  totalAvailableOre: number
  totalRefinedAllTime: number
  lockedDepositsCount: number
  availableDepositsCount: number
}

export interface OrePagination {
  page: number
  limit: number
  total: number
}

export interface OreResponse {
  summary: OreSummary
  deposits: OreDepositData[]
  visible?: boolean
  // Optional on the wire to keep tooling/tests resilient to older mocks; the
  // current backend always returns it. The store guards with `if (response.pagination)`.
  pagination?: OrePagination
}

export interface FetchOreOptions {
  page?: number
  limit?: number
}

export interface RefineResponse {
  success: boolean
  error?: string
  totalAvailable?: number
  refinedPercentage?: number
  refinedAmount?: number
  newBalance?: string
  depositsRefined?: number
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Fetch ore data for the authenticated user.
 */
export async function fetchOre(
  accessToken: string,
  options: FetchOreOptions = {},
): Promise<OreResponse> {
  const config = useRuntimeConfig()
  const sessionId = typeof window !== 'undefined'
    ? localStorage.getItem('aurumrun_session_id')
    : null

  const query: Record<string, number> = {}
  if (options.page !== undefined) query.page = options.page
  if (options.limit !== undefined) query.limit = options.limit

  return await $fetch<OreResponse>(`${config.public.apiBase}/rakeback`, {
    query,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })
}

/**
 * Refine all available ore.
 * Returns the refine result with random percentage (3-10%) and amount.
 */
export async function refineOre(accessToken: string): Promise<RefineResponse> {
  const config = useRuntimeConfig()
  const sessionId = typeof window !== 'undefined'
    ? localStorage.getItem('aurumrun_session_id')
    : null

  return await $fetch<RefineResponse>(`${config.public.apiBase}/rakeback-claim`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })
}
